/* Judge.me public widget API only. Never use a private API token here. */
(() => {
  'use strict';
  const requests = new Map();

  async function publishedSummary(domain, token) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    async function widget(name, page) {
      const url = new URL(`https://api.judge.me/api/v1/widgets/${name}`);
      url.searchParams.set('shop_domain', domain);
      url.searchParams.set('api_token', token);
      if (page) url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
      if (!response.ok) throw new Error('Review widget unavailable');
      return response.json();
    }
    try {
      const [countData, ratingData] = await Promise.all([widget('shop_reviews_count'), widget('shop_reviews_rating')]);
      const products = new Map();
      let complete = false;
      for (let page = 1; page <= 100; page += 1) {
        const data = await widget('all_reviews_page', page);
        if (typeof data.all_reviews !== 'string') throw new Error('Invalid review response');
        // Parse detached markup for published product scores; never insert API HTML.
        const document = new DOMParser().parseFromString(data.all_reviews, 'text/html');
        const reviews = document.querySelectorAll('.jdgm-rev[data-review-id]');
        if (!reviews.length) { complete = true; break; }
        const previousSize = products.size;
        reviews.forEach((review) => {
          const id = review.getAttribute('data-review-id');
          const score = Number(review.querySelector('.jdgm-rev__rating[data-score]')?.getAttribute('data-score'));
          if (!id || !Number.isInteger(score) || score < 1 || score > 5) throw new Error('Invalid review score');
          products.set(id, score);
        });
        if (products.size === previousSize) throw new Error('Review pagination did not advance');
      }
      const storeCount = Number(countData.shop_reviews_count);
      const storeRating = Number(ratingData.shop_reviews_rating);
      if (!complete || !Number.isInteger(storeCount) || storeCount < 0 || !Number.isFinite(storeRating) || storeRating < 0 || storeRating > 5) throw new Error('Incomplete review summary');
      const count = products.size + storeCount;
      const sum = [...products.values()].reduce((total, score) => total + score, 0) + storeCount * storeRating;
      return { count, rating: count ? sum / count : 0 };
    } finally { clearTimeout(timeout); }
  }

  function install(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const section = root.closest('.shopify-section');
    if (!section) return;
    const attach = () => {
      const header = section.querySelector('.jdgm-header');
      if (!header) return false;
      if (root.parentElement !== header) header.append(root);
      return true;
    };
    if (!attach()) {
      const observer = new MutationObserver(() => { if (attach()) observer.disconnect(); });
      observer.observe(section, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 20000);
    }
    const key = root.dataset.shopDomain;
    if (!requests.has(key)) requests.set(key, publishedSummary(key, root.dataset.publicToken));
    requests.get(key).then(({ count, rating }) => {
      if (!count) return;
      const locale = document.documentElement.lang || 'sv';
      const text = root.querySelector('[data-review-summary-text]');
      const label = root.dataset.summaryLabel
        .replace('__rating__', new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rating))
        .replace('__count__', new Intl.NumberFormat(locale).format(count));
      text.textContent = label;
      root.style.setProperty('--review-fill', `${rating / 5 * 100}%`);
      root.dataset.reviewCount = String(count);
      root.dataset.reviewRating = rating.toFixed(2);
      root.hidden = false;
    }).catch(() => {
      // Keep actual review cards visible, but never show a stale or partial total.
      root.hidden = true;
      root.dataset.status = 'unavailable';
    });
  }
  function scan(scope = document) { scope.querySelectorAll('[data-cc-review-summary]').forEach(install); }
  scan();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();